import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dashboardAPI as API } from '../../../axiosConfig';

const URL = '/groups';

const SLICE_NAME = 'userManagementGroups';
const LIST_USER_MANAGEMENT_GROUPS = `${SLICE_NAME}/user-management/groups/list`;
const GET_USER_MANAGEMENT_GROUP_DETAIL = `${SLICE_NAME}/user-management/groups/detail`;
const CREATE_USER_MANAGEMENT_GROUP = `${SLICE_NAME}/user-management/groups/create`;
const UPDATE_USER_MANAGEMENT_GROUP = `${SLICE_NAME}/user-management/groups/update`;
const DELETE_USER_MANAGEMENT_GROUP = `${SLICE_NAME}/user-management/groups/delete`;
const ADD_USER_TO_GROUP = `${SLICE_NAME}/user-management/groups/add-user`;
const REMOVE_USER_FROM_GROUP = `${SLICE_NAME}/user-management/groups/remove-user`;

export const getUserManagementGroupList = createAsyncThunk(
  LIST_USER_MANAGEMENT_GROUPS,
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await API.get(URL);

      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const getUserManagementGroupDetail = createAsyncThunk(
  GET_USER_MANAGEMENT_GROUP_DETAIL,
  async (id, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await API.get(`${URL}/${id}`);

      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const createGroup = createAsyncThunk(
  CREATE_USER_MANAGEMENT_GROUP,
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await API.post(URL, payload);
      return data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const updateGroup = createAsyncThunk(
  UPDATE_USER_MANAGEMENT_GROUP,
  async ({ id, ...payload }, { rejectWithValue }) => {
    const url = `${URL}/${id}`;

    try {
      const { data } = await API.put(url, payload);
      return data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const deleteGroup = createAsyncThunk(
  DELETE_USER_MANAGEMENT_GROUP,
  async ({ id }, { rejectWithValue }) => {
    const url = `${URL}/${id}`;

    try {
      const { data } = await API.delete(url);
      return data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const addUserToGroup = createAsyncThunk(
  ADD_USER_TO_GROUP,
  async ({ groupId, ...payload }, { rejectWithValue }) => {
    const url = `${URL}/${groupId}/users`;

    try {
      const { data } = await API.post(url, payload);
      return data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error?.response?.data);
    }
  },
);

export const removeUserFromGroup = createAsyncThunk(
  REMOVE_USER_FROM_GROUP,
  async ({ groupId, userId }, { rejectWithValue }) => {
    const url = `${URL}/${groupId}/users/${userId}`;

    try {
      const { data } = await API.delete(url);
      return data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error?.response?.data);
    }
  },
);

const initialState = {
  groupList: [],
  groupListStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed',
  groupDetails: {},
  groupDetailStatus: {},
  deleteStatus: 'idle',
  addUserStatus: 'idle',
  removeUserStatus: 'idle',
};

const userManagementGroupsSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserManagementGroupList.pending, (state) => {
        state.groupListStatus = 'loading';
      })
      .addCase(getUserManagementGroupList.rejected, (state) => {
        state.groupList = [];
        state.groupListStatus = 'failed';
      })
      .addCase(getUserManagementGroupList.fulfilled, (state, { payload }) => {
        state.groupList = payload;
        state.groupListStatus = 'succeeded';
      })
      .addCase(getUserManagementGroupDetail.pending, (state, { meta }) => {
        state.groupDetailStatus[meta.arg] = 'loading';
      })
      .addCase(getUserManagementGroupDetail.rejected, (state, { meta }) => {
        state.groupDetailStatus[meta.arg] = 'failed';
      })
      .addCase(
        getUserManagementGroupDetail.fulfilled,
        (state, { payload, meta }) => {
          state.groupDetails[meta.arg] = payload;
          state.groupDetailStatus[meta.arg] = 'succeeded';
        },
      )
      .addCase(deleteGroup.pending, (state) => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteGroup.fulfilled, (state) => {
        state.deleteStatus = 'succeeded';
      })
      .addCase(deleteGroup.rejected, (state) => {
        state.deleteStatus = 'failed';
      })
      .addCase(addUserToGroup.pending, (state) => {
        state.addUserStatus = 'loading';
      })
      .addCase(addUserToGroup.fulfilled, (state) => {
        state.addUserStatus = 'succeeded';
      })
      .addCase(addUserToGroup.rejected, (state) => {
        state.addUserStatus = 'failed';
      })
      .addCase(removeUserFromGroup.pending, (state) => {
        state.removeUserStatus = 'loading';
      })
      .addCase(removeUserFromGroup.fulfilled, (state) => {
        state.removeUserStatus = 'succeeded';
      })
      .addCase(removeUserFromGroup.rejected, (state) => {
        state.removeUserStatus = 'failed';
      });
  },
});

export const userManagementGroupsActions = userManagementGroupsSlice.actions;

export const selectUserManagementGroupsList = (state) =>
  state.userManagement.groups.groupList;

export const selectIsLoadingUserManagementGroupsList = (state) =>
  state.userManagement.groups.groupListStatus === 'loading';

export const selectUserManagementGroupDetails = (state) =>
  state.userManagement.groups.groupDetails;

export const selectUserManagementGroupDetailStatuses = (state) =>
  state.userManagement.groups.groupDetailStatus;

export const selectIsDeletingGroup = (state) =>
  state.userManagement.groups.deleteStatus === 'loading';

export const selectIsAddingUserToGroup = (state) =>
  state.userManagement.groups.addUserStatus === 'loading';

export const selectIsRemovingUserFromGroup = (state) =>
  state.userManagement.groups.removeUserStatus === 'loading';

export default userManagementGroupsSlice.reducer;
